<script setup>
import { Link, useForm } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import Loading from 'vue-loading-overlay';
import { Carousel, Slide } from 'vue3-carousel';
import { ref, onMounted } from "vue";
import FlatPickr from 'vue-flatpickr-component';

const props = defineProps({
    year: String,
    featuredPackages: Object,
    hotelData: Object,
    countries: Object,
    flash: Object,
    user: Object,
});
let isLoading = ref(false);

const checkin = ref({
    allowInput: true,
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
    minDate: 'today',
});
const checkout = ref({
    allowInput: true,
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});

onMounted(() => {
    const readonlyInput = document.querySelectorAll('input[readonly]');
    readonlyInput.forEach((item) => {
        item.style.backgroundColor = '#eceef14a';
    });
});

function handleDateChange() {
    checkout.value = {
        minDate: BookSearchform.checkin
    };
}

// id=childrenage should be added dynamically depending on the number of children. function to do this.
function addChildrenAge() {
    const roomstemparr = BookSearchform.children_age;
    BookSearchform.children_age = [];
    for (let i = 0; i < BookSearchform.noof_children; i++) {
        BookSearchform.children_age.push({
            age: 0,
        });
    }
    //preventing the data loss when the user changes the number of rooms
    for (let i = 0; i < roomstemparr.length; i++) {
        if (i < BookSearchform.children_age.length) {
            BookSearchform.children_age[i] = roomstemparr[i];
        }
    }
}

//function to calculate the number of nights
function calculateNights() {
    const checkinDate = new Date(BookSearchform.checkin);
    const checkoutDate = new Date(BookSearchform.checkout);
    const diffTime = Math.abs(checkoutDate - checkinDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}

const BookSearchform = useForm({
    staytype: 0,
    hotel: 'all',
    checkin: '',
    checkout: '',
    adults: 1,
    noof_children: 0,
    no_rooms: 1,
    children_age: [],
    nationality: '0',
});

const BookSearchFormSubmit = () => {
    BookSearchform.get('/bookings/searchForPackages', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
};

</script>
<style scoped>
@import "vue-loading-overlay/dist/vue-loading.css";
@import 'vue3-carousel/dist/carousel.css';
@import 'flatpickr/dist/themes/material_orange.css';

.carousel__item {
    min-height: 400px;
    width: 100%;
    background-color: #ffffff;
    color: #FFFFFF;
    font-size: 20px;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.carousel__slide {
    padding: 10px;
}

.carousel__prev,
.carousel__next {
    box-sizing: content-box;
    border: 5px solid white;
}
</style>
<template>
    <AppLayout title="Bookings Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <Carousel :pauseAutoplayOnHover=true :wrapAround=true :autoplay=4000>
                <Slide v-for="slide in featuredPackages" :key="slide">
                    <div class="carousel__item">
                        <img :src="'/fetchImages/d?id=' + slide.hotel.id + '&type=cover&w=1368&h=400&fit=crop&border=400,1000,overlay'"
                            :alt="slide.hotel.name" class="rounded-top rounded-bottom" />
                        <div style="position: absolute;top: 70%;left: 50%;transform: translate(-50%, -50%);">
                            <span class="display-5" style="text-shadow: 2px 2px #000;">{{
                                slide.hotel.name
                            }}</span>
                            <div class="mb-3"></div>
                            <span class="display-6" style="text-shadow: 2px 2px #000;">{{
                                slide.package_name
                            }}</span><br>
                            <span class="btn btn-warning mt-4"><strong>Book Package</strong></span>
                        </div>
                    </div>
                </Slide>
            </Carousel>

            <div class="nav-align-top mb-3 mt-4">
                <ul class="nav nav-tabs" role="tablist">
                    <li class="nav-item">
                        <button class="nav-link active" data-bs-toggle="tab" data-bs-target="#form-tabs-personal" role="tab"
                            aria-selected="true">
                            Book Package
                        </button>
                    </li>
                </ul>
                <div class="tab-content">
                    <div class="tab-pane fade active show" id="form-tabs-personal" role="tabpanel">
                        <form @submit.prevent="BookSearchFormSubmit">
                            <div class="row g-3">
                                <div class="col-md-4">
                                    <label class="form-label" for="formtabs-first-name">Stay Type</label>
                                    <select class="form-select" v-model="BookSearchform.staytype" required>
                                        <option value="0">Single Stay</option>
                                        <option value="1">Split Stay (Same Hotel)</option>
                                    </select>
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label" for="formtabs-last-name">Hotel</label>
                                    <select class="form-select" v-model="BookSearchform.hotel" required>
                                        <option value="all">All Hotels</option>
                                        <option v-for="hotel in hotelData" :value="hotel.id">{{ hotel.name }}</option>
                                    </select>
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label" for="formtabs-last-name">Nationality</label>
                                    <select class="form-select" v-model="BookSearchform.nationality" required>
                                        <option selected disabled>Select Nationality...</option>
                                        <option v-for="country in countries" :value="country.id">{{ country.name }}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-md-3">
                                    <label class="form-label" for="formtabs-country">Check-in</label>
                                    <FlatPickr :config="checkin" v-model="BookSearchform.checkin" class="form-control"
                                        placeholder="Check-in Date" @change="handleDateChange" required />
                                </div>

                                <div class="col-md-3">
                                    <label class="form-label" for="formtabs-country">Check-out</label>
                                    <FlatPickr :config="checkout" v-model="BookSearchform.checkout" class="form-control"
                                        placeholder="Check-out Date" required />
                                    <span class="badge bg-label-danger mt-2"
                                        v-if="BookSearchform.checkout && BookSearchform.checkin">
                                        {{ calculateNights() }} Nights</span>
                                </div>
                                <div class="col-md-2">
                                    <label class="form-label" for="adults">Adults</label>
                                    <select class="form-select" required="required" v-model="BookSearchform.adults">
                                        <option value="" disabled="disabled">Number of Adults</option>
                                        <option value="1">1 Adult</option>
                                        <option value="2" selected="selected">2 Adults</option>
                                        <option value="3">3 Adults</option>
                                        <option value="4">4 Adults</option>
                                    </select>
                                </div>
                                <div class="col-md-2">
                                    <label class="form-label" for="formtabs-last-name">Kids</label>
                                    <select name="noof_children" class="form-select mb-2"
                                        v-model="BookSearchform.noof_children" @change="addChildrenAge">
                                        <option :value="0">No Children</option>
                                        <option :value="1">1 Child</option>
                                        <option :value="2">2 Children</option>
                                        <option :value="3">3 Children</option>
                                    </select>

                                    <span id="childrenage" v-if="BookSearchform.noof_children > 0"
                                        v-for="(room, index) in BookSearchform.children_age" :key="index">
                                        <select class="form-select mb-2" v-model="room.age" required>
                                            <option value="needed" selected disabled>Age needed</option>
                                            <option value="0">0 year old</option>
                                            <option value="1">1 Year</option>
                                            <option value="2">2 Years</option>
                                            <option value="3">3 Years</option>
                                            <option value="4">4 Years</option>
                                            <option value="5">5 Years</option>
                                            <option value="6">6 Years</option>
                                            <option value="7">7 Years</option>
                                            <option value="8">8 Years</option>
                                            <option value="9">9 Years</option>
                                            <option value="10">10 Years</option>
                                            <option value="11">11 Years</option>
                                            <option value="12">12 Years</option>
                                            <option value="13">13 Years</option>
                                            <option value="14">14 Years</option>
                                            <option value="15">15 Years</option>
                                            <option value="16">16 Years</option>
                                            <option value="17">17 Years</option>
                                        </select>
                                    </span>
                                </div>
                                <div class="col-md-2">
                                    <label class="form-label" for="formtabs-last-name">Number of rooms</label>
                                    <select class="form-select" v-model="BookSearchform.no_rooms" required="required">
                                        <option value="" disabled="disabled" selected="selected">Number of Rooms
                                        </option>
                                        <option value="1">1 Room</option>
                                        <option value="2">2 Rooms</option>
                                        <option value="3">3 Rooms</option>
                                        <option value="4">4 Rooms</option>
                                        <option value="5">5 Rooms</option>
                                    </select>
                                </div>

                            </div>
                            <div class="pt-4">
                                <button type="submit" class="btn btn-primary me-sm-3 me-1">Search Packages</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>